import React from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_HOME } from "@/lib/seo-defs";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowRight, Sparkles, Target, ListChecks, CheckCircle2, X, TrendingUp, ShieldCheck,
  Users, Briefcase, Building2, LineChart, ClipboardCheck, BadgeCheck,
  Zap, Crown, Compass, Lightbulb, BarChart3, FileSearch,
  Factory, HardHat, Cog, Globe, Cpu, Boxes,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const ProblemCard = ({ icon: Icon, title, desc, challenge }) => (
  <Link to={`/checkup?challenge=${encodeURIComponent(challenge)}`} className="group" data-testid={`problem-card-${challenge}`}>
    <div className="cxo-card p-5 h-full flex flex-col gap-2 group-hover:-translate-y-0.5 transition-transform">
      <div className="w-10 h-10 rounded-lg bg-[#EAF0FF] text-[#2D6BFF] flex items-center justify-center">
        <Icon size={18} />
      </div>
      <h4 className="text-base font-semibold text-slate-900 mt-1">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
      <div className="mt-auto pt-2 inline-flex items-center text-xs font-medium text-[#2D6BFF] gap-1">Avvia check-up <ArrowRight size={12} /></div>
    </div>
  </Link>
);

const SolutionCard = ({ icon: Icon, title, desc, tags }) => (
  <div className="cxo-card p-6">
    <div className="w-11 h-11 rounded-xl bg-[#071225] text-white flex items-center justify-center mb-4">
      <Icon size={20} />
    </div>
    <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
    <div className="mt-4 flex flex-wrap gap-1.5">
      {tags.map((t) => (
        <Badge key={t} variant="secondary" className="bg-slate-100 text-slate-700 border-0 font-medium">{t}</Badge>
      ))}
    </div>
  </div>
);

const StepCard = ({ num, title, desc }) => (
  <div className="flex flex-col gap-3">
    <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#2D6BFF] text-white text-sm font-bold cxo-tabular">{num}</div>
    <h4 className="text-base font-semibold text-slate-900">{title}</h4>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

const BenefitCard = ({ icon: Icon, title, desc }) => (
  <div className="cxo-card p-5">
    <Icon size={20} className="text-[#2D6BFF]" />
    <h4 className="mt-3 text-base font-semibold text-slate-900">{title}</h4>
    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

export default function HomePage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen">
      <SEO {...SEO_HOME} />
      <PublicHeader />

      {/* === HERO === */}
      <section className="relative pt-28 pb-20 sm:pt-32 sm:pb-28 cxo-hero-gradient text-white overflow-hidden cxo-noise" data-testid="home-hero">
        <div className="absolute inset-0 cxo-grid-bg opacity-30 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Badge className="bg-white/10 border border-white/20 text-white hover:bg-white/15">
                <Sparkles size={12} className="mr-1.5" /> {t("hero.badge")}
              </Badge>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
                {t("hero.title_main")} <span className="text-[#7AA0FF]">{t("hero.title_accent")}</span>
              </h1>
              <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl">
                {t("hero.subtitle")}
              </p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/checkup">
                  <Button size="lg" className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white shadow-lg shadow-blue-900/40 h-12 px-6 text-base font-semibold w-full sm:w-auto" data-testid="home-hero-primary-cta">
                    {t("hero.cta_primary")}
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/per-le-imprese">
                  <Button size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white h-12 px-6 text-base w-full sm:w-auto" data-testid="home-hero-secondary-cta">
                    {t("hero.cta_secondary")}
                  </Button>
                </Link>
              </div>
              <p className="mt-3 text-xs text-white/60">{t("hero.cta_note")}</p>

              <div className="mt-7 inline-flex items-start gap-3 p-3 sm:p-4 bg-white/5 border border-white/10 rounded-xl max-w-xl">
                <Compass size={18} className="shrink-0 mt-0.5 text-[#7AA0FF]" />
                <p className="text-sm text-white/80 leading-relaxed">{t("hero.flexibility")}</p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-xl bg-white/[0.06] backdrop-blur border border-white/10 p-6 sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/60">{t("hero.journey")}</p>
                <div className="mt-4 space-y-3">
                  {[
                    { icon: FileSearch, ...t("hero.journey_steps.need", { returnObjects: true }) },
                    { icon: Users, ...t("hero.journey_steps.selection", { returnObjects: true }) },
                    { icon: Briefcase, ...t("hero.journey_steps.project", { returnObjects: true }) },
                    { icon: BarChart3, ...t("hero.journey_steps.kpi", { returnObjects: true }) },
                    { icon: TrendingUp, ...t("hero.journey_steps.results", { returnObjects: true }) },
                  ].map((row, i) => {
                    const Icon = row.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                        <div className="w-9 h-9 rounded-md bg-[#2D6BFF]/20 text-[#9BB6FF] flex items-center justify-center">
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/60">{row.label}</p>
                          <p className="text-sm font-semibold text-white">{row.value}</p>
                        </div>
                        <CheckCircle2 size={16} className="text-[#5FCB95]" />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-white/60">
                  <span>{t("hero.apply")}</span>
                  <Link to="/per-i-manager" className="text-[#9BB6FF] hover:text-white font-medium">{t("nav.for_managers")} →</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === PROBLEMA === */}
      <section className="py-16 sm:py-24 bg-white" data-testid="home-problem-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0 hover:bg-[#EAF0FF]">Il problema</Badge>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              La tua azienda sta crescendo, ma non puoi gestire tutto da solo.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Molte PMI hanno competenze tecniche eccellenti, clienti e opportunità di crescita. Spesso, però, mancano il tempo, i metodi o le figure manageriali necessarie per organizzare meglio il lavoro, controllare i costi, delegare attività e affrontare progetti complessi.
            </p>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              Assumere un dirigente esperto a tempo pieno può essere troppo costoso. Affidarsi a consulenze generiche, invece, non sempre porta qualcuno realmente dentro i problemi quotidiani dell’azienda. <strong className="text-slate-900">CXOORA nasce per colmare questo vuoto.</strong>
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <ProblemCard icon={TrendingUp} title="La crescita sta creando disorganizzazione?" desc="Ordini, persone, sedi: l'azienda cresce, le strutture no." challenge="crescita non governata" />
            <ProblemCard icon={LineChart} title="I margini sono difficili da controllare?" desc="Manca un controllo di gestione strutturato." challenge="margini insufficienti" />
            <ProblemCard icon={Crown} title="Troppe decisioni dipendono dal titolare?" desc="Serve introdurre deleghe e responsabilità chiare." challenge="difficoltà nel delegare" />
            <ProblemCard icon={Cog} title="Processi frammentati o lenti?" desc="Strumenti scollegati, attività ripetitive, perdita di tempo." challenge="processi inefficienti" />
            <ProblemCard icon={Cpu} title="Devi digitalizzare l'azienda?" desc="Dati e processi sparsi: serve una direzione tecnologica." challenge="digitalizzazione" />
            <ProblemCard icon={Zap} title="Vuoi migliorare la produttività?" desc="Ottimizzare le operations senza fermare la macchina." challenge="processi inefficienti" />
            <ProblemCard icon={Users} title="Stai affrontando un passaggio generazionale?" desc="Struttura, deleghe e governance per il futuro." challenge="passaggio generazionale" />
            <ProblemCard icon={Globe} title="Vuoi entrare in nuovi mercati?" desc="Export e internazionalizzazione con competenze adeguate." challenge="export" />
            <ProblemCard icon={Target} title="Un progetto strategico senza guida esperta?" desc="Roadmap, milestone e KPI per non disperdere energie." challenge="gestione di un progetto complesso" />
          </div>
        </div>
      </section>

      {/* === SOLUZIONE 3 CARD === */}
      <section className="py-16 sm:py-24 bg-[#F6F8FC]" data-testid="home-solution-cards">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <Badge className="bg-[#E9F8F1] text-[#1F9D6A] border-0 hover:bg-[#E9F8F1]">La soluzione</Badge>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Una competenza da grande azienda, accessibile anche alla tua PMI.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              CXOORA seleziona manager con esperienza reale e li inserisce nella tua azienda con una formula flessibile. Non si limita a consegnare un documento: lavora con te, condivide gli obiettivi, supporta il team e contribuisce a trasformare le decisioni in risultati concreti.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            <SolutionCard icon={ShieldCheck} title="Competenze senior" desc="Accedi a professionisti con esperienza manageriale senza dover sostenere il costo di un'assunzione full-time." tags={["CEO", "CFO", "COO", "CTO", "CHRO"]} />
            <SolutionCard icon={Compass} title="Impegno flessibile" desc="Scegli il supporto realmente necessario: pochi giorni al mese, una presenza ricorrente o un progetto circoscritto." tags={["Pochi giorni/mese", "1-2 giorni/sett.", "Progetto"]} />
            <SolutionCard icon={BarChart3} title="Risultati misurabili" desc="Ogni incarico parte da obiettivi chiari, milestone e indicatori per verificare l'impatto del lavoro svolto." tags={["KPI", "Milestone", "Timeline"]} />
          </div>
        </div>
      </section>

      {/* === PRIMA / DOPO === */}
      <section className="py-16 sm:py-24 bg-white" data-testid="home-before-after">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Da un problema difficile da gestire a un percorso chiaro.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="cxo-card p-6 sm:p-7 bg-slate-50 border-slate-200">
              <Badge className="bg-slate-200 text-slate-700 border-0">Prima</Badge>
              <ul className="mt-5 space-y-3">
                {[
                  "Troppe decisioni concentrate sulla proprietà.",
                  "Priorità poco chiare.",
                  "Ruoli non definiti.",
                  "Costi difficili da controllare.",
                  "Processi lenti o frammentati.",
                  "Progetti importanti che procedono senza una guida.",
                  "Crescita affidata prevalentemente all’intuizione.",
                ].map((t) => (
                  <li key={t} className="flex gap-2.5 text-sm text-slate-600"><X size={16} className="text-slate-400 mt-0.5 shrink-0" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="cxo-card p-6 sm:p-7 border-2 border-[#2D6BFF]/30">
              <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">Con CXOORA</Badge>
              <ul className="mt-5 space-y-3">
                {[
                  "Un manager selezionato sulla base del bisogno reale.",
                  "Obiettivi condivisi.",
                  "Responsabilità più chiare.",
                  "KPI per misurare i risultati.",
                  "Piano operativo.",
                  "Supporto concreto al team.",
                  "Costi proporzionati all’effettivo utilizzo.",
                ].map((t) => (
                  <li key={t} className="flex gap-2.5 text-sm text-slate-700"><CheckCircle2 size={16} className="text-[#1F9D6A] mt-0.5 shrink-0" /><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-8 max-w-2xl">
            <p className="text-base sm:text-lg font-display font-semibold text-slate-900 leading-snug">
              “Non acquisti soltanto ore di consulenza. Inserisci competenze direzionali nel punto in cui possono generare maggiore valore.”
            </p>
          </div>
        </div>
      </section>

      {/* === ESEMPIO CONCRETO === */}
      <section className="py-16 sm:py-24 bg-[#F6F8FC]" data-testid="home-example">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">Caso concreto</Badge>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Come funziona nella pratica?</h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Un'impresa di impiantistica sta crescendo rapidamente. Ha acquisito nuove commesse, ma iniziano ad aumentare ritardi, difficoltà di coordinamento e problemi nella pianificazione delle squadre. <strong className="text-slate-900">Assumere un Direttore Operativo a tempo pieno sarebbe troppo oneroso.</strong>
            </p>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              Attraverso CXOORA, l'azienda individua un <strong className="text-slate-900">Fractional COO</strong>: un manager esperto che entra nel team uno o due giorni alla settimana, analizza i processi, chiarisce le responsabilità, introduce KPI e aiuta l'impresa a organizzare la crescita.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { num: 1, title: "Analizziamo la situazione", desc: "Check-up guidato per identificare i blocchi e le opportunità." },
              { num: 2, title: "Individuiamo la competenza", desc: "Il ruolo manageriale più utile alla tua azienda." },
              { num: 3, title: "Selezioniamo il manager", desc: "Shortlist curata di max 3 profili senior verificati." },
              { num: 4, title: "Monitoriamo i risultati", desc: "Milestone, KPI e timeline in dashboard condivisa." },
            ].map((s) => (
              <div key={s.num} className="cxo-card p-5">
                <div className="w-9 h-9 rounded-full bg-[#2D6BFF] text-white flex items-center justify-center text-sm font-bold cxo-tabular">{s.num}</div>
                <h4 className="mt-3 text-base font-semibold text-slate-900">{s.title}</h4>
                <p className="mt-1.5 text-sm text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <p className="text-sm font-semibold text-slate-700">Altri esempi di ruoli che possiamo attivare:</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                "CFO per margini e controllo",
                "COO per operations e produttività",
                "CTO per digitalizzazione",
                "CHRO per ruoli e persone",
                "Export Manager per nuovi mercati",
                "Energy Manager per consumi e costi",
                "PMO per progetti complessi",
                "Procurement per acquisti e fornitori",
              ].map((r) => (
                <Badge key={r} variant="outline" className="bg-white border-slate-200 text-slate-700 font-medium">{r}</Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === DIFFERENZIAZIONE === */}
      <section className="py-16 sm:py-24 bg-white" data-testid="home-differentiation">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Badge className="bg-slate-100 text-slate-700 border-0">Differenziazione</Badge>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Non devi scegliere tra centinaia di profili. Partiamo dal tuo problema.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              CXOORA non è un marketplace generalista e non ti lascia solo nella scelta. Attraverso un check-up iniziale analizziamo le priorità della tua azienda. La piattaforma individua le competenze più coerenti e il team CXOORA valida una shortlist ristretta di manager selezionati.
            </p>
            <p className="mt-3 text-base text-slate-600">
              L'obiettivo non è proporti il maggior numero possibile di professionisti, ma individuare quelli realmente adatti alla tua situazione.
            </p>
            <Link to="/checkup" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D6BFF] hover:text-[#1F57F2]">
              Avvia il check-up gratuito <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { n: 1, t: "Raccontaci la tua azienda", d: "Compila il check-up guidato in pochi minuti." },
              { n: 2, t: "Ricevi una selezione mirata", d: "Shortlist curata di max 3 manager validati." },
              { n: 3, t: "Avvia un percorso misurabile", d: "Obiettivi, milestone e KPI in dashboard condivisa." },
            ].map((it) => (
              <div key={it.n} className="cxo-card p-5 flex flex-col gap-2 sm:gap-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 cxo-tabular">Step 0{it.n}</span>
                <h4 className="text-base font-semibold text-slate-900">{it.t}</h4>
                <p className="text-sm text-slate-600">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === COME FUNZIONA === */}
      <section className="py-16 sm:py-24 bg-[#071225] text-white relative overflow-hidden" data-testid="home-how-it-works">
        <div className="absolute inset-0 cxo-grid-bg opacity-30" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <Badge className="bg-white/10 text-white border border-white/20">Come funziona</Badge>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              Un percorso semplice. Un supporto concreto.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { n: 1, t: "Raccontaci la tua azienda", d: "Completa il check-up gratuito e descrivi la sfida che vuoi affrontare." },
              { n: 2, t: "Individuiamo la priorità", d: "Analizziamo il bisogno e identifichiamo la competenza manageriale più utile." },
              { n: 3, t: "Selezioniamo il manager", d: "Ricevi una shortlist curata di professionisti senior compatibili." },
              { n: 4, t: "Misuriamo i risultati", d: "Gestisci il progetto attraverso obiettivi, milestone, documenti e KPI." },
            ].map((s) => (
              <div key={s.n} className="p-5 rounded-xl bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-full bg-[#2D6BFF] text-white flex items-center justify-center text-base font-bold cxo-tabular">{s.n}</div>
                <h4 className="mt-3 text-base font-semibold">{s.t}</h4>
                <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === BENEFITS === */}
      <section className="py-16 sm:py-24 bg-white" data-testid="home-benefits">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">Perché CXOORA</Badge>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Una soluzione concreta per aziende che vogliono fare un salto di qualità.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <BenefitCard icon={Crown} title="Esperienza manageriale accessibile" desc="Competenze senior reali, alla portata delle PMI." />
            <BenefitCard icon={Compass} title="Costi scalabili" desc="Proporzionati al bisogno effettivo. Niente fissi pesanti." />
            <BenefitCard icon={BadgeCheck} title="Manager verificati" desc="Profili senior con esperienza documentata e referenze." />
            <BenefitCard icon={ClipboardCheck} title="Matching assistito" desc="Algoritmo + validazione manuale del team CXOORA." />
            <BenefitCard icon={Briefcase} title="Approccio operativo" desc="Non solo analisi: il manager entra nel team e lavora con te." />
            <BenefitCard icon={Target} title="Obiettivi, milestone, KPI" desc="Tutto visibile e tracciabile nella piattaforma." />
          </div>
        </div>
      </section>

      {/* === A CHI È UTILE === */}
      <section className="py-16 sm:py-24 bg-[#F6F8FC]" data-testid="home-audience">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              CXOORA è pensata per la tua azienda se…
            </h2>
          </div>
          <Tabs defaultValue="pmi" className="mt-8">
            <TabsList className="bg-white border border-slate-200 p-1">
              <TabsTrigger value="pmi" className="data-[state=active]:bg-[#071225] data-[state=active]:text-white">PMI in crescita</TabsTrigger>
              <TabsTrigger value="family" className="data-[state=active]:bg-[#071225] data-[state=active]:text-white">Familiari</TabsTrigger>
              <TabsTrigger value="startup" className="data-[state=active]:bg-[#071225] data-[state=active]:text-white">Startup & Scale-up</TabsTrigger>
              <TabsTrigger value="tech" className="data-[state=active]:bg-[#071225] data-[state=active]:text-white">Imprese tecniche</TabsTrigger>
            </TabsList>
            <TabsContent value="pmi" className="mt-6">
              <AudienceList items={["Stai crescendo più velocemente della tua organizzazione.","Vuoi rendere l'azienda meno dipendente dal titolare.","Devi affrontare un progetto strategico.","Hai bisogno di competenze senior, ma non di una figura full-time.","Vuoi introdurre metodi più strutturati.","Devi migliorare il controllo economico.",]} />
            </TabsContent>
            <TabsContent value="family" className="mt-6">
              <AudienceList items={["Decisioni concentrate prevalentemente sulla proprietà.","Necessità di introdurre deleghe.","Necessità di definire ruoli e responsabilità.","Passaggio generazionale.","Crescita difficile da governare.","Necessità di rendere l'azienda meno dipendente dal titolare.",]} />
            </TabsContent>
            <TabsContent value="startup" className="mt-6">
              <AudienceList items={["Crescita rapida.","Fundraising.","Industrializzazione.","Sviluppo commerciale.","Necessità di controllo finanziario.","Competenze senior senza poter sostenere un executive full-time.",]} />
            </TabsContent>
            <TabsContent value="tech" className="mt-6">
              <AudienceList items={["Impiantistica, costruzioni, energia, engineering.","Manifattura, logistica, facility management.","Gestione commesse e manutenzione.","Supply chain.","Transizione digitale ed energetica.","Team distribuiti territorialmente.",]} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* === COMPARISON TABLE === */}
      <section className="py-16 sm:py-24 bg-white" data-testid="home-comparison-table">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Perché scegliere una formula fractional?
            </h2>
            <p className="mt-3 text-base text-slate-600">Confronto sintetico delle alternative più comuni.</p>
          </div>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[700px] cxo-card overflow-hidden">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 p-4">Caratteristica</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 p-4">Assunzione full-time</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 p-4">Consulenza tradizionale</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 p-4">Marketplace freelance</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#2D6BFF] p-4 bg-[#EAF0FF]/40">CXOORA</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  ["Competenza senior", "Sì", "Variabile", "Variabile", "Sì - verificata"],
                  ["Flessibilità", "Bassa", "Media", "Alta", "Alta"],
                  ["Coinvolgimento operativo", "Alto", "Basso", "Variabile", "Alto"],
                  ["Selezione assistita", "Recruiting interno", "Tu scegli", "Tu scegli", "Shortlist curata"],
                  ["Obiettivi e KPI", "Da impostare", "Da definire", "Da definire", "Inclusi"],
                  ["Costo", "Elevato e fisso", "Variabile", "Variabile", "Proporzionato all'uso"],
                ].map((row, i) => (
                  <tr key={i} className="border-b border-slate-200 last:border-0">
                    <td className="p-4 font-semibold text-slate-900">{row[0]}</td>
                    <td className="p-4 text-slate-600">{row[1]}</td>
                    <td className="p-4 text-slate-600">{row[2]}</td>
                    <td className="p-4 text-slate-600">{row[3]}</td>
                    <td className="p-4 font-semibold text-[#071225] bg-[#EAF0FF]/30">
                      <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#1F9D6A]" />{row[4]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* === FINAL CTA === */}
      <section className="py-16 sm:py-24 bg-[#F6F8FC]" data-testid="home-final-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="cxo-card overflow-hidden">
            <div className="p-8 sm:p-12 bg-gradient-to-br from-[#071225] to-[#0B1B33] text-white relative overflow-hidden cxo-noise">
              <div className="absolute inset-0 cxo-grid-bg opacity-30" />
              <div className="relative max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                  Non sempre serve assumere un nuovo dirigente.<br />
                  <span className="text-[#7AA0FF]">Serve trovare quello giusto.</span>
                </h2>
                <p className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed">
                  Completa il check-up gratuito. Ti aiuteremo a capire quale competenza manageriale può generare maggiore valore nella tua azienda.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Link to="/checkup">
                    <Button size="lg" className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white h-12 px-6 text-base font-semibold w-full sm:w-auto" data-testid="home-cta-final-primary">
                      Avvia il check-up gratuito <ArrowRight size={18} className="ml-2" />
                    </Button>
                  </Link>
                  <Link to="/per-le-imprese">
                    <Button size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white h-12 px-6 text-base w-full sm:w-auto" data-testid="home-cta-final-secondary">
                      Prenota una call conoscitiva
                    </Button>
                  </Link>
                </div>
                <p className="mt-3 text-xs text-white/60">
                  Nessun impegno. Nessuna selezione casuale. Solo una prima analisi concreta del tuo bisogno.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const AudienceList = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {items.map((it) => (
      <div key={it} className="flex items-start gap-2.5 cxo-card p-4">
        <CheckCircle2 size={18} className="text-[#2D6BFF] shrink-0 mt-0.5" />
        <span className="text-sm text-slate-700">{it}</span>
      </div>
    ))}
  </div>
);
